import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import { categories, mainSiteUrl } from '@/mocks/categories';

export default function Navbar({ transparent = false }: { transparent?: boolean }) {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [catsOpen, setCatsOpen] = useState(false);

  useEffect(() => {
    function onScroll() {
      setScrolled(window.scrollY > 24);
    }
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // When transparent, show light text over the hero until scrolled; otherwise always solid.
  const solid = !transparent || scrolled;

  const linkBase =
    'whitespace-nowrap rounded-md px-3 py-2 text-sm font-semibold transition-colors';
  const linkColor = solid
    ? 'text-stone-800 hover:bg-stone-100 hover:text-emerald-700'
    : 'text-white hover:bg-white/15 hover:text-white';

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        solid
          ? 'border-b border-stone-200 bg-white/95 backdrop-blur'
          : 'border-b border-transparent bg-transparent'
      }`}
    >
      <nav className="mx-auto flex h-16 w-full items-center justify-between px-4 md:px-6">
        <Link
          to="/"
          className={`flex items-center gap-2 whitespace-nowrap font-heading text-lg font-extrabold ${
            solid ? 'text-stone-900' : 'text-white'
          }`}
          onClick={() => setMenuOpen(false)}
        >
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-emerald-500 text-white">
            <i className="ri-stack-line"></i>
          </span>
          <span>USFansLink</span>
        </Link>

        <div className="hidden items-center gap-1 lg:flex">
          <Link to="/" className={`${linkBase} ${linkColor}`} onClick={() => setMenuOpen(false)}>
            {t('nav.home')}
          </Link>
          <a href="#categories" className={`${linkBase} ${linkColor}`}>
            {t('nav.categories')}
          </a>
          <a href="#spreadsheet" className={`${linkBase} ${linkColor}`}>
            {t('nav.guides')}
          </a>
          <Link
            to="/article/usfans-shopping-guide"
            className={`${linkBase} ${linkColor}`}
            onClick={() => setMenuOpen(false)}
          >
            {t('nav.shoppingGuide')}
          </Link>
          <a href="#faq" className={`${linkBase} ${linkColor}`}>
            {t('nav.faq')}
          </a>
        </div>

        <div className="flex items-center gap-2">
          <div className="hidden lg:block">
            <LanguageSwitcher light={solid} />
          </div>
          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className="hidden whitespace-nowrap rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-emerald-700 md:block"
          >
            {t('nav.openSpreadsheet')}
          </a>
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            className={`flex h-10 w-10 items-center justify-center rounded-md text-2xl lg:hidden ${
              solid ? 'text-stone-800' : 'text-white'
            }`}
            aria-label="Menu"
          >
            <i className={menuOpen ? 'ri-close-line' : 'ri-menu-line'}></i>
          </button>
        </div>
      </nav>

      {menuOpen && (
        <div className="max-h-[calc(100vh-4rem)] overflow-y-auto border-t border-stone-200 bg-white px-4 py-4 lg:hidden">
          <div className="flex flex-col gap-1">
            <Link
              to="/"
              onClick={() => setMenuOpen(false)}
              className="whitespace-nowrap rounded-md px-3 py-2.5 text-sm font-semibold text-stone-800 hover:bg-stone-100"
            >
              {t('nav.home')}
            </Link>

            {/* Mobile categories */}
            <button
              type="button"
              onClick={() => setCatsOpen((v) => !v)}
              className="flex w-full items-center justify-between whitespace-nowrap rounded-md px-3 py-2.5 text-sm font-semibold text-stone-800 hover:bg-stone-100"
            >
              {t('nav.categories')}
              <span className="flex h-5 w-5 items-center justify-center text-base text-stone-400">
                <i className={catsOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}></i>
              </span>
            </button>
            {catsOpen && (
              <div className="grid grid-cols-2 gap-1 rounded-md bg-stone-50 p-2">
                {categories.map((cat) => (
                  <a
                    key={cat.key}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-2 whitespace-nowrap rounded-md px-2.5 py-2 text-sm text-stone-700 hover:bg-white"
                  >
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center text-sm text-emerald-600">
                      <i className={cat.icon}></i>
                    </span>
                    {t(`categories.${cat.key}`)}
                  </a>
                ))}
              </div>
            )}

            <a
              href="#spreadsheet"
              onClick={() => setMenuOpen(false)}
              className="whitespace-nowrap rounded-md px-3 py-2.5 text-sm font-semibold text-stone-800 hover:bg-stone-100"
            >
              {t('nav.guides')}
            </a>
            <Link
              to="/article/usfans-shopping-guide"
              onClick={() => setMenuOpen(false)}
              className="whitespace-nowrap rounded-md px-3 py-2.5 text-sm font-semibold text-stone-800 hover:bg-stone-100"
            >
              {t('nav.shoppingGuide')}
            </Link>
            <a
              href="#faq"
              onClick={() => setMenuOpen(false)}
              className="whitespace-nowrap rounded-md px-3 py-2.5 text-sm font-semibold text-stone-800 hover:bg-stone-100"
            >
              {t('nav.faq')}
            </a>

            <div className="mt-2 border-t border-stone-100 pt-3">
              <LanguageSwitcher light />
            </div>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="mt-2 whitespace-nowrap rounded-md bg-emerald-600 px-4 py-2.5 text-center text-sm font-semibold text-white transition-colors hover:bg-emerald-700"
            >
              {t('nav.openSpreadsheet')}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}